// ---------------------------------------------------------------------------
// Donut chart — pure data → SVG string
// ---------------------------------------------------------------------------

import { svgText, svgAttr, polarToCartesian, numberFormat, round2 } from './svg-util.js';
import { chartTitleDesc } from './chart-a11y.js';

/** Default slice palette (theme-overridable via CSS `fill`); index-stable. */
const DEFAULT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#64748b'];

/**
 * @typedef {Object} DonutChartOpts
 * @property {string} idPrefix
 * @property {string} [title]
 * @property {string} [desc]
 * @property {number} [size]
 * @property {number} [thickness] - ring thickness as a fraction of radius (0..1).
 */

/**
 * Render a donut (ring) chart as an SVG string. Each slice is an annular arc.
 * A single non-zero slice renders as a full ring. Colors come from the slice
 * `color` if present, else the default palette by index.
 *
 * @param {{ label: string, value: number, color?: string }[]} slices
 * @param {DonutChartOpts} opts
 * @returns {string}
 */
export function donutChart(slices, opts) {
  const { idPrefix, title = 'Donut chart', desc = '', size = 160, thickness = 0.4 } = opts;
  const safe = (Array.isArray(slices) ? slices : []).filter((s) => (Number(s?.value) || 0) > 0);
  const total = safe.reduce((sum, s) => sum + (Number(s?.value) || 0), 0);
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 2;
  const rInner = round2(rOuter * (1 - Math.min(0.9, Math.max(0.1, thickness))));

  const { titleId, descId, svgDefs } = chartTitleDesc(
    title,
    desc || `Donut chart with ${safe.length} segments.`,
    idPrefix,
  );

  let startAngle = 0;
  const arcs = safe
    .map((s, i) => {
      const value = Number(s.value) || 0;
      const frac = total > 0 ? value / total : 0;
      // A single full-circle slice can't be drawn as one arc (start === end);
      // render it as a full ring via two stacked circles instead.
      if (frac >= 1) {
        const color = svgAttr(s.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]);
        return (
          `<circle class="chart-donut-slice" cx="${round2(cx)}" cy="${round2(cy)}" r="${round2((rOuter + rInner) / 2)}" ` +
          `fill="none" stroke="${color}" stroke-width="${round2(rOuter - rInner)}">` +
          `<title>${svgText(`${s.label}: ${numberFormat(value)}`)}</title></circle>`
        );
      }
      const endAngle = startAngle + frac * 360;
      const largeArc = endAngle - startAngle > 180 ? 1 : 0;
      const oStart = polarToCartesian(cx, cy, rOuter, startAngle);
      const oEnd = polarToCartesian(cx, cy, rOuter, endAngle);
      const iEnd = polarToCartesian(cx, cy, rInner, endAngle);
      const iStart = polarToCartesian(cx, cy, rInner, startAngle);
      const color = svgAttr(s.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length]);
      const d =
        `M ${round2(oStart.x)} ${round2(oStart.y)} ` +
        `A ${round2(rOuter)} ${round2(rOuter)} 0 ${largeArc} 1 ${round2(oEnd.x)} ${round2(oEnd.y)} ` +
        `L ${round2(iEnd.x)} ${round2(iEnd.y)} ` +
        `A ${round2(rInner)} ${round2(rInner)} 0 ${largeArc} 0 ${round2(iStart.x)} ${round2(iStart.y)} Z`;
      startAngle = endAngle;
      return (
        `<path class="chart-donut-slice" d="${d}" fill="${color}">` +
        `<title>${svgText(`${s.label}: ${numberFormat(value)}`)}</title></path>`
      );
    })
    .join('');

  return (
    `<svg class="chart chart--donut" viewBox="0 0 ${svgAttr(size)} ${svgAttr(size)}" ` +
    `preserveAspectRatio="xMidYMid meet" aria-labelledby="${svgAttr(titleId)} ${svgAttr(descId)}">` +
    svgDefs +
    arcs +
    `</svg>`
  );
}
